// @ts-strict-ignore
import { Mesh, Object3D } from 'three';
import { AdmintoolComponentsPresenter } from '../components/presenter/admintool-components.presenter';
import { Injectable } from '@angular/core';
import { AdminToolService } from '../services/admintool.service';
import { ArticleExportManager } from '../manager/article-export.manager';
import { WxArticleVO } from '@article/model/vo/wx-article.vo';
import { ArticleIdMappingVO, ArticleMetadataVO, ArticleTypeVO, ArticleVO } from '@article/model/vo/article.vo';
import { ExportToolArticleVO } from '@article/model/vo/export-tool-article.vo';
import { GlobalContainer } from '@3d-scene/model3d/global-container';
import { ObjInfo } from '@article/model/obj-info';
import { ObjExporter } from '@3d-scene/exporters/obj-exporter';
import { ProjectUserModel } from '@project/model/project-user.model';
import { OBJRepresentationDataVO } from '@shared/vo/representation.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ExportArticleModelDTO } from '@article/model/vo/export-article-model.dto';
import { Dictionary } from 'typescript-collections';
import { ConnectionAreaVO, MetaElementVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { WxConnectionPlaneMesh } from './wx-connection-plane-mesh';
import { MeshUtils } from '@3d-scene/common/util/mesh-utils';
import { ExportToolUtils } from './export-tool-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ConnectionCreator } from '@article/components/connections/connection-creation/connection-creator';
import { MetaElementUtils } from '@article/components/connections/connection-creation/meta-element.utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { WxConnectionElement } from './wx-connection-element';
import { ExportToolDataService } from './export-tool-data.service';
import { lastValueFrom } from 'rxjs';
import { ConfiguratorParameters } from '@3d-scene/configurator-parameters/configurator-parameters';
import { StringUtils } from '@shared/util/string-utils';

@Injectable({providedIn: 'root'})
export class ExportToolService {

  public static readonly TEXTURE_AREA_ID: number = 5;
  public static readonly MULTIPURPOSE_CONNECTION_ID: number = 10;


  constructor(private exportToolDataService: ExportToolDataService,
              private windowService: AdmintoolComponentsPresenter,
              private adminToolService: AdminToolService,
              private articleExportManager: ArticleExportManager,
              private globalContainer: GlobalContainer,
              private projectUserModel: ProjectUserModel,
              private articleContainerUtils: ArticleContainerUtils,
  ) {
  }

  public async saveArticle(article: ArticleVO, wxArticle?: WxArticleVO): Promise<boolean> {
    const metaData = await  this.openManageArticleMetaInfoDialog(wxArticle);
    if (!metaData) {
      return false;
    }
    const exportToolArticle: ExportToolArticleVO = this.extractArticle(this.globalContainer, metaData);
    exportToolArticle.isImportedFile = article.isImportedFile;
    const exporter: ObjExporter = new ObjExporter(this.projectUserModel);
      const globalContainerData: ObjInfo = exporter.export(
        this.globalContainer,
        true,
        true,
        false,
        false,
        true,
        ConfiguratorParameters.inverseTextureCoordinates,
        false,
        (object3D: Object3D): Boolean => {
          return (
            this.articleContainerUtils.isObjectRelevant(object3D) &&
            object3D.name !== Connection.DEFAULT_CONNECTION_OUTLINE_NAME
          );
        });
    const objRepresentationData: OBJRepresentationDataVO = this.articleExportManager.convertObjInfoToRepresentationData(globalContainerData);

    const exportArticleModelDTO = new ExportArticleModelDTO();
    exportArticleModelDTO.data = objRepresentationData;
    exportArticleModelDTO.article = exportToolArticle;
    await lastValueFrom(this.exportToolDataService.executeSaveArticle(exportArticleModelDTO));
    await this.adminToolService.loadArticle(exportToolArticle.articleNo);
    return true;
  }

  private openManageArticleMetaInfoDialog(wxArticle: WxArticleVO): Promise<ArticleMetadataVO> {
    const articleMetadata: ArticleMetadataVO = new ArticleMetadataVO();
    articleMetadata.articleId = wxArticle ? wxArticle.itemId : null;
    articleMetadata.articleType = new ArticleTypeVO();
    articleMetadata.articleType.id = wxArticle ? wxArticle.articleType : null;
    articleMetadata.articleIdMapping = new ArticleIdMappingVO();
    articleMetadata.articleIdMapping.oldArticleId = wxArticle ? wxArticle.itemId : null;
    articleMetadata.articleIdMapping.articleNo = wxArticle ? wxArticle.itemNo : null;
    articleMetadata.articleIdMapping.eanNo = wxArticle ? wxArticle.articleEAN : null;
    articleMetadata.articleIdMapping.materialStatus = null;
    articleMetadata.articleIdMapping.lastCall = null;
    articleMetadata.articleIdMapping.minimumStockLevel = null;
    articleMetadata.articleIdMapping.publicGlobal = true;
    articleMetadata.articleIdMapping.isFullArticle = true;
    return this.windowService.openManageArticleMetaInfoDialog(null, articleMetadata);
  }

  private extractArticle(globalContainer: GlobalContainer, metaData: ArticleMetadataVO): ExportToolArticleVO {
    const article: ExportToolArticleVO = new ExportToolArticleVO();
    article.identity = metaData.articleId;
    article.articleNo = metaData.articleIdMapping.articleNo;
    article.version = 0;
    article.type = metaData.articleType;
    article.oldArticleId = metaData.articleIdMapping.oldArticleId;
    article.eanNo = metaData.articleIdMapping.eanNo;
    article.materialStatus = metaData.articleIdMapping.materialStatus;
    article.minimumStockLevel = metaData.articleIdMapping.minimumStockLevel;
    article.publicGlobal = metaData.articleIdMapping.publicGlobal;
    article.isFullArticle = metaData.articleIdMapping.isFullArticle;
    const metaElements: MetaElementVO[] = [];
    const metaElementsNameDictionary: Dictionary<string, boolean> = new Dictionary();
    this.extractMetaElements(globalContainer, metaElements, metaElementsNameDictionary);
    article.metaElements = metaElements;
    return article;
  }

  private extractMetaElements(object3D: Object3D, connectionElements: MetaElementVO[], metaElementsNameDictionary: Dictionary<string, boolean>):void {
    const createdInternalConnections = [];
    for(const child of object3D.children) {
      if (child instanceof WxConnectionPlaneMesh && child?.connectionElement?.active) {
        child.connectionName = this.getNextName(child.connectionName, metaElementsNameDictionary);
        const connections = this.createConnectionElements(child.connectionElement, child.connectionName);
        connectionElements.push(...connections);
        if (connections.length > 1) {
          const internalConnection: Mesh = MeshUtils.cloneMesh(child.firstFlatMesh);
          ExportToolUtils.rotateConnectionByNormal(internalConnection, MathUtils.toRadians(ConnectionCreator.INTERNAL_CONNECTION_ROTATION));
          internalConnection.name = MetaElementUtils.applyInternalConnectionPostfix(child.connectionName);
          internalConnection.visible = false;
          createdInternalConnections.push(internalConnection);
        }
      } else {
        this.extractMetaElements(child, connectionElements, metaElementsNameDictionary);
      }
    }
    if (ArrayUtils.isNotEmpty(createdInternalConnections)) {
      object3D.add(...createdInternalConnections);
    }
  }

  private getNextName(initName: string, nameDictionary: Dictionary<string, boolean>): string {
    let name: string = initName;
    let index: number = 2;
    while(nameDictionary.hasOwnProperty(name)) {
      name = initName + "_" + index;
      index++;
    }
    nameDictionary[name] = true;
    return name;
  }

  private createConnectionElements(connection: WxConnectionElement, connectionName: string): MetaElementVO[] {
    if (connection === null) {
      return null;
    }
    const connectionElements: MetaElementVO[] = [];
    let articleConnection: ConnectionAreaVO = null;
    let textConnection: TextureAreaVO = null;
    let isInternal: boolean = false;
    if (connection.typeId === ExportToolService.MULTIPURPOSE_CONNECTION_ID) {
      articleConnection = new ConnectionAreaVO();
      const connectionSetId: string = StringUtils.createUuid();
      MetaElementUtils.setMultipurposeConnectionSetId(articleConnection, connectionSetId);
      articleConnection.connectionSide = ConnectionAreaVO.SIDE_ACCEPTING;
      textConnection = new TextureAreaVO();
      MetaElementUtils.setMultipurposeConnectionSetId(textConnection, connectionSetId);
      isInternal = true;
    }
    else if (connection.typeId === ExportToolService.TEXTURE_AREA_ID) {
      textConnection = new TextureAreaVO();
    } else {
      articleConnection = new ConnectionAreaVO();
      articleConnection.connectionSide = (connection.direction === true) ? ConnectionAreaVO.SIDE_ACCEPTING : ConnectionAreaVO.SIDE_GIVING;
    }

    if (textConnection != null) {
      this.updateConnectionElement(textConnection, connection, connectionName, isInternal);
      connectionElements.push(textConnection);
    }
    if (articleConnection != null) {
      this.updateConnectionElement(articleConnection, connection, connectionName, false);
      connectionElements.push(articleConnection);
    }

    return connectionElements;
  }

  private updateConnectionElement(modelConnection: MetaElementVO, connection: WxConnectionElement, connectionName: string, isInternal: boolean): void {
    if (modelConnection != null) {
      if (isInternal) {
        connectionName = MetaElementUtils.applyInternalConnectionPostfix(connectionName);
        modelConnection.isInternal = true;
      }
      modelConnection.name = connectionName;
      modelConnection.corridor = connection.corridor;
      modelConnection.orderNumber = connection.connectionOrder;
    }
  }

}
